'use client';

// TODO(resources-hub-cleanup): DELETE this slice after the redesign release. The
// `resources_center` slice (legacy hero) is only used by the `blog_home_layout`
// root, which is being retired. Not in the `page`/`resources_hub` palette. Remove
// this folder, its entry in `blogListingComponents`, and (regenerate) `slices/index.ts`.

import { FC, useRef, useState, useCallback, useEffect } from 'react';
import { Content, isFilled, asLink } from '@prismicio/client';
import { PrismicRichText, SliceComponentProps } from '@prismicio/react';
import { PrismicNextLink } from '@prismicio/next';
import Link from 'next/link';
import { EbookDocument } from '../../../prismicio-types';
import type { UpcomingWebinar } from '@/utils/upcoming-webinars';

const EVENT_GRADIENTS = [
  'linear-gradient(180deg, rgb(4,17,24) 47%, rgba(0,0,0,0) 100%), linear-gradient(270deg, rgb(4,17,24) 0%, rgb(19,9,74) 50%, rgb(4,17,24) 100%)',
  'linear-gradient(180deg, rgb(4,17,24) 47%, rgba(0,0,0,0) 100%), linear-gradient(270deg, rgb(4,17,24) 0%, rgb(75,15,234) 48%, rgb(4,17,24) 100%)',
];

interface ResourcesCenterContext {
  featuredEbooks?: EbookDocument[];
  upcomingWebinars?: UpcomingWebinar[];
}

/**
 * Props for `ResourcesCenter`.
 */
export type ResourcesCenterProps = SliceComponentProps<
  Content.ResourcesCenterSlice,
  ResourcesCenterContext
>;

/**
 * Component for "ResourcesCenter" Slices.
 */
const ResourcesCenter: FC<ResourcesCenterProps> = ({ slice, context }) => {
  const { section_title, section_description, featured_resources } =
    slice.primary;

  // Dynamic data from server context
  const dynamicEbooks = context?.featuredEbooks;
  const upcomingWebinars = (context?.upcomingWebinars ?? []).slice(0, 2);
  const hasDynamicEbooks = dynamicEbooks && dynamicEbooks.length > 0;
  const carouselCount = hasDynamicEbooks
    ? dynamicEbooks.length
    : (featured_resources?.length ?? 0);

  // Default to the middle card so the carousel opens centered on the most
  // visually prominent slot, with the other cards "peeking" at the edges.
  const initialSlide = Math.max(0, Math.floor(carouselCount / 2));
  const [currentSlide, setCurrentSlide] = useState(initialSlide);
  const carouselRef = useRef<HTMLDivElement>(null);

  const scrollToSlide = useCallback(
    (index: number, behavior: ScrollBehavior = 'smooth') => {
      if (!carouselRef.current) {
        return;
      }
      const container = carouselRef.current;
      const gap = 24;
      const cardWidth = container.firstElementChild
        ? (container.firstElementChild as HTMLElement).offsetWidth + gap
        : 0;
      container.scrollTo({ left: cardWidth * index, behavior });
      setCurrentSlide(index);
    },
    [],
  );

  // Position the carousel at the middle card on mount (without animation so
  // the user doesn't see a sweep on first paint).
  useEffect(() => {
    if (initialSlide > 0) {
      scrollToSlide(initialSlide, 'auto');
    }
    // Intentionally one-shot on mount.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleScroll = useCallback(() => {
    if (!carouselRef.current) {
      return;
    }
    const container = carouselRef.current;
    const gap = 24;
    const cardWidth = container.firstElementChild
      ? (container.firstElementChild as HTMLElement).offsetWidth + gap
      : 1;
    const newSlide = Math.round(container.scrollLeft / cardWidth);
    setCurrentSlide(newSlide);
  }, []);

  return (
    <section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      className="relative z-20 pb-12 pt-[168px] lg:pb-16 lg:pt-[156px] landscape-short:pt-24"
    >
      {/* Header area — frosted-glass card with pill, title, description, and
          upcoming webinar cards (matches Figma 18895:6717). */}
      <div className="container mx-auto px-6 pb-10">
        <div className="backdrop-blur-[10px] bg-black/20 rounded-[40px] p-10 flex flex-col items-center gap-4">
          {/* Pill */}
          <div className="border border-[#00F557] rounded-full px-4 py-2">
            <span className="text-white text-sm font-normal uppercase tracking-wide">
              RESOURCES HUB
            </span>
          </div>

          {/* Title */}
          {isFilled.richText(section_title) && (
            <PrismicRichText
              field={section_title}
              components={{
                heading1: ({ children }) => (
                  <h1 className="h1-hero text-center text-white">{children}</h1>
                ),
              }}
            />
          )}

          {/* Description */}
          {isFilled.richText(section_description) && (
            <div className="text-lg text-white/90 text-center max-w-2xl">
              <PrismicRichText field={section_description} />
            </div>
          )}

          {/* Upcoming webinar cards (max 2, dynamically pulled). Empty state
              shows a "no webinars" message — matches prior UpcomingWebinars UX. */}
          {upcomingWebinars.length > 0 ? (
            <div className="flex flex-col sm:flex-row gap-4 w-full mt-2">
              {upcomingWebinars.map((webinar, i) => {
                const month = webinar.eventDate
                  .toLocaleString('en-US', { month: 'short' })
                  .toUpperCase();
                const day = webinar.eventDate.getDate();
                return (
                  <Link
                    key={webinar.uid || i}
                    href={`/resources-center/webinar/${webinar.uid}`}
                    className="flex-1 min-w-0 flex items-center gap-6 px-6 py-4 rounded-3xl transition-opacity hover:opacity-90"
                    style={{
                      backgroundImage:
                        EVENT_GRADIENTS[i % EVENT_GRADIENTS.length],
                    }}
                  >
                    {/* Date badge */}
                    <div
                      className="shrink-0 w-[50px] h-[50px] rounded-lg border border-[#00F557] flex flex-col items-center justify-around py-1"
                      style={{
                        background:
                          'linear-gradient(112.83deg, rgba(0,0,0,0.25) 0%, rgba(255,255,255,0) 110.84%)',
                      }}
                    >
                      <span className="text-white text-[11px] font-semibold font-manrope leading-none">
                        {month}
                      </span>
                      <div className="w-full border-t border-white/30" />
                      <span className="text-white text-lg font-semibold font-manrope leading-none">
                        {day}
                      </span>
                    </div>
                    {/* Webinar title */}
                    <p className="flex-1 min-w-0 text-[#00F557] text-base font-bold font-manrope leading-snug capitalize">
                      {webinar.title}
                    </p>
                  </Link>
                );
              })}
            </div>
          ) : (
            <p className="mt-2 text-center font-manrope text-base font-bold text-[#00F557]">
              No upcoming webinars — check back soon!
            </p>
          )}
        </div>
      </div>

      {/* Carousel — shows dynamic featured ebooks or static resources */}
      {carouselCount > 0 && (
        <div className="mt-10">
          <div
            ref={carouselRef}
            onScroll={handleScroll}
            className="no-scrollbar flex snap-x snap-mandatory gap-6 overflow-x-auto px-5 md:px-[max(20px,calc((100vw-1092px)/2))]"
          >
            {hasDynamicEbooks
              ? dynamicEbooks.map((ebook, index) => (
                  <div
                    key={ebook.id || index}
                    className="relative flex min-h-[279px] w-[calc(100vw-40px)] flex-shrink-0 snap-center flex-col items-start justify-between overflow-hidden rounded-2xl border-2 border-white p-5 shadow-[0px_4px_65px_0px_rgba(0,0,0,0.08)] sm:p-6 md:h-[279px] md:w-[min(1092px,calc(100vw-40px))]"
                    style={
                      ebook.data?.image?.url
                        ? {
                            backgroundImage: `url(${ebook.data.image.url})`,
                            backgroundSize: 'cover',
                            backgroundPosition: 'center',
                          }
                        : undefined
                    }
                  >
                    {/* Dark overlay */}
                    {ebook.data?.image?.url && (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 bg-black/45"
                      />
                    )}

                    {/* Type badge */}
                    <span
                      className="relative z-10 inline-flex items-center rounded-[20px] border border-white/70 px-6 py-1.5 text-xs font-bold uppercase tracking-[2.4px] text-white backdrop-blur-[21px]"
                      style={{
                        backgroundImage:
                          'linear-gradient(148deg, rgba(255,255,255,0.47) 0%, rgba(255,255,255,0) 111%)',
                      }}
                    >
                      EBOOK
                    </span>

                    {/* Bottom content */}
                    <div className="relative z-10 flex w-full items-center gap-6">
                      <div className="flex flex-1 flex-col gap-3 md:gap-2">
                        <div className="flex items-center border-l border-[#00f557] pl-2 py-1">
                          <span className="text-xs font-semibold uppercase text-white">
                            Guides, Whitepapers &amp; Ebooks
                          </span>
                        </div>
                        <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:gap-6">
                          <div className="flex-1">
                            <PrismicRichText
                              field={ebook.data?.name}
                              components={{
                                paragraph: ({ children }) => (
                                  <p className="text-lg font-bold leading-snug text-white md:text-2xl">
                                    {children}
                                  </p>
                                ),
                                heading1: ({ children }) => (
                                  <p className="text-lg font-bold leading-snug text-white md:text-2xl">
                                    {children}
                                  </p>
                                ),
                              }}
                            />
                          </div>
                          <Link
                            href={
                              asLink(ebook.data?.link_to_landing_page) ||
                              ebook.url ||
                              '#'
                            }
                            className="w-full shrink-0 rounded-full bg-[#00f557] px-6 py-3 text-center text-base font-bold text-[#002726] transition-opacity hover:opacity-90 md:w-[200px]"
                          >
                            Get Ebook
                          </Link>
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              : featured_resources.map((resource, index) => (
                  <div
                    key={index}
                    className="relative flex min-h-[279px] w-[calc(100vw-40px)] flex-shrink-0 snap-center flex-col items-start justify-between overflow-hidden rounded-2xl border-2 border-white p-5 shadow-[0px_4px_65px_0px_rgba(0,0,0,0.08)] sm:p-6 md:h-[279px] md:w-[min(1092px,calc(100vw-40px))]"
                    style={
                      isFilled.image(resource.resource_image)
                        ? {
                            backgroundImage: `url(${resource.resource_image.url})`,
                            backgroundSize: 'fill',
                            backgroundPosition: 'center',
                          }
                        : undefined
                    }
                  >
                    {/* Dark overlay */}
                    {isFilled.image(resource.resource_image) && (
                      <div
                        aria-hidden="true"
                        className="pointer-events-none absolute inset-0 bg-black/45"
                      />
                    )}

                    {/* Resource label badge */}
                    {resource.resource_label && (
                      <span
                        className="relative z-10 inline-flex items-center rounded-[20px] border border-white/70 px-6 py-1.5 text-xs font-bold uppercase tracking-[2.4px] text-white backdrop-blur-[21px]"
                        style={{
                          backgroundImage:
                            'linear-gradient(148deg, rgba(255,255,255,0.47) 0%, rgba(255,255,255,0) 111%)',
                        }}
                      >
                        {resource.resource_label}
                      </span>
                    )}

                    {/* Bottom content */}
                    <div className="relative z-10 flex w-full items-center gap-6">
                      <div className="flex flex-1 flex-col gap-3 md:gap-2">
                        {/* Resource type with green left border */}
                        {resource.resource_type && (
                          <div className="flex items-center border-l border-[#00f557] pl-2 py-1">
                            <span className="text-xs font-semibold uppercase text-white">
                              {resource.resource_type}
                            </span>
                          </div>
                        )}

                        {/* Resource title + CTA row */}
                        <div className="flex flex-col items-start gap-3 md:flex-row md:items-center md:gap-6">
                          <div className="flex-1">
                            {isFilled.richText(resource.resource_title) && (
                              <PrismicRichText
                                field={resource.resource_title}
                                components={{
                                  paragraph: ({ children }) => (
                                    <p className="text-lg font-bold leading-snug text-white md:text-2xl">
                                      {children}
                                    </p>
                                  ),
                                }}
                              />
                            )}
                          </div>

                          {isFilled.link(resource.resource_cta) && (
                            <PrismicNextLink
                              field={resource.resource_cta}
                              className="w-full shrink-0 rounded-full bg-[#00f557] px-6 py-3 text-center text-base font-bold text-[#002726] transition-opacity hover:opacity-90 md:w-[200px]"
                            >
                              {resource.resource_cta.text || 'Learn More'}
                            </PrismicNextLink>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
          </div>

          {/* Pagination dots — white pills sized for visibility on the dark
              hero background. Active dot is filled, inactive is outlined. */}
          {carouselCount > 1 && (
            <div className="mt-6 flex items-center justify-center gap-2">
              {Array.from({ length: carouselCount }).map((_, i) => (
                <button
                  key={i}
                  onClick={() => scrollToSlide(i)}
                  className={`h-2 w-16 rounded-full transition-all duration-300 ${
                    i === currentSlide
                      ? 'bg-white/60'
                      : 'border border-white/30 bg-transparent'
                  }`}
                  aria-label={`Go to slide ${i + 1}`}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </section>
  );
};

export default ResourcesCenter;
